import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { NotFoundPage } from '@/components/NotFoundPage';
import { RouteErrorPage } from '@/components/RouteErrorPage';
import { Spinner } from '@mmef/shared/components';
import { RequireAuth } from '@mmef/shared/auth';
import { RequireAnonymous } from '@mmef/shared/auth';
import { EventsPage } from '@/features/events/EventsPage';
import { EventDetailPage } from '@/features/events/EventDetailPage';

/**
 * Everything a first-time visitor sees (the events list and a detail page) ships in the main
 * bundle. The rest — forms, account management, moderation — is code-split: most visitors never
 * open them, and the moderation screens exist for a handful of operators.
 */
const SubmitEventPage = lazy(() =>
  import('@/features/events/SubmitEventPage').then((m) => ({ default: m.SubmitEventPage })),
);
// Lazy like the rest: read rarely, and there's no reason for them to cost every visitor a
// download before they've seen an event.
const PrivacyNoticePage = lazy(() =>
  import('@/features/legal/PrivacyNoticePage').then((m) => ({ default: m.PrivacyNoticePage })),
);
const TermsPage = lazy(() =>
  import('@/features/legal/TermsPage').then((m) => ({ default: m.TermsPage })),
);
// Straight at the modules, not the barrel: the barrel is statically imported by the store and the
// nav, so lazily importing it splits nothing — Vite says as much with INEFFECTIVE_DYNAMIC_IMPORT.
const SignInPage = lazy(() =>
  import('@mmef/shared/auth/SignInPage').then((m) => ({ default: m.SignInPage })),
);
const VerifyPage = lazy(() =>
  import('@mmef/shared/auth/VerifyPage').then((m) => ({ default: m.VerifyPage })),
);
const AccountLayout = lazy(() =>
  import('@/features/account/AccountLayout').then((m) => ({ default: m.AccountLayout })),
);
const ProfilePage = lazy(() =>
  import('@/features/account/ProfilePage').then((m) => ({ default: m.ProfilePage })),
);
const SavedEventsPage = lazy(() =>
  import('@/features/account/SavedEventsPage').then((m) => ({ default: m.SavedEventsPage })),
);
const MyEventsPage = lazy(() =>
  import('@/features/account/MyEventsPage').then((m) => ({ default: m.MyEventsPage })),
);
const EditMyEventPage = lazy(() =>
  import('@/features/account/EditMyEventPage').then((m) => ({ default: m.EditMyEventPage })),
);
const PrivacyPage = lazy(() =>
  import('@/features/account/PrivacyPage').then((m) => ({ default: m.PrivacyPage })),
);
const SubscriptionsPage = lazy(() =>
  import('@/features/subscriptions/SubscriptionsPage').then((m) => ({ default: m.SubscriptionsPage })),
);
// Operator screens live in the admin app now — a separate origin, so they can be firewalled and
// are never served to a public visitor.
const OrganisersPage = lazy(() =>
  import('@/features/organisers/OrganisersPage').then((m) => ({ default: m.OrganisersPage })),
);
const EventbriteCallbackPage = lazy(() =>
  import('@/features/organisers/EventbriteCallbackPage').then((m) => ({
    default: m.EventbriteCallbackPage,
  })),
);

const withSuspense = (element: ReactNode) => <Suspense fallback={<Spinner />}>{element}</Suspense>;

export const router = createBrowserRouter([
  {
    element: <Layout />,
    errorElement: <RouteErrorPage />,
    children: [
      { index: true, element: <EventsPage /> },
      { path: 'events/:id', element: <EventDetailPage /> },
      { path: 'submit', element: withSuspense(<SubmitEventPage />) },
      // Public and outside any auth guard on purpose: a privacy notice has to be readable before
      // someone hands over an email address, not after.
      { path: 'privacy', element: withSuspense(<PrivacyNoticePage />) },
      { path: 'terms', element: withSuspense(<TermsPage />) },
      {
        // Signing in when you already are is a dead end, so it redirects home instead.
        element: <RequireAnonymous />,
        children: [{ path: 'sign-in', element: withSuspense(<SignInPage />) }],
      },
      // Path fixed by the backend: AuthProperties#magicLinkUrl emails /auth/verify?token=…
      { path: 'auth/verify', element: withSuspense(<VerifyPage />) },
      {
        element: <RequireAuth />,
        children: [
          {
            // Sidebar shell shared by every account section, so each page is just its content.
            element: withSuspense(<AccountLayout />),
            children: [
              { path: 'account', element: <Navigate to="/account/profile" replace /> },
              { path: 'account/profile', element: withSuspense(<ProfilePage />) },
              { path: 'account/alerts', element: withSuspense(<SubscriptionsPage />) },
              { path: 'account/saved', element: withSuspense(<SavedEventsPage />) },
              { path: 'account/events', element: withSuspense(<MyEventsPage />) },
              // Linked from the import email. Someone else's event 404s rather than 403s, so this
              // can't be used to discover which ids exist.
              { path: 'account/events/:id', element: withSuspense(<EditMyEventPage />) },
              { path: 'account/privacy', element: withSuspense(<PrivacyPage />) },
              // Stays at /organisers rather than moving under /account: the Eventbrite OAuth
              // redirect URI is registered against this path, and renaming it would break linking
              // until the app was re-registered. It still renders inside the account shell.
              { path: 'organisers', element: withSuspense(<OrganisersPage />) },
            ],
          },
          // Old path, kept so existing links and bookmarks don't break.
          { path: 'account/subscriptions', element: <Navigate to="/account/alerts" replace /> },
          // Deliberately outside the shell: a transient page you pass through, not somewhere to
          // navigate around from. The path must stay in step with
          // muslimevents.eventbrite.oauth.redirect-uri and the Eventbrite app settings.
          { path: 'organisers/eventbrite/callback', element: withSuspense(<EventbriteCallbackPage />) },
        ],
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
